<template>
  <VContainer fluid class="pa-0">
    <VMenu
      :model-value="open"
      :activator="activator"
      width="360px"
      :close-on-content-click="false"
      :scrim="false"
      transition="none"
      @update:model-value="val => emits(val ? 'open' : 'close')"
      @mouseleave="emits('leave')"
      @mouseenter="emits('enter')"
    >
      <template #default>
        <VCard class="menu-content pa-0 mt-5" variant="flat">
          <CategoriesMenu
            :base-category="baseCategory"
            @close="emits('close')"
          />
        </VCard>
      </template>
    </VMenu>
  </VContainer>
</template>

<script setup lang="ts">
import type { CategoryItem } from '~/types/root'
import CategoriesMenu from '~/components/layout/header/menu/CategoriesMenu.vue'

interface Props {
  activator?: string
  categories: CategoryItem[]
  baseCategory: CategoryItem
  open?: boolean
  isMainMenu?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  activator: 'parent',
  open: false,
  isMainMenu: false,
})

const emits = defineEmits<{
  (e: 'leave' | 'enter' | 'close' | 'open'): void
  (e: 'navigate', value: string): void
}>()

// The @update:model-value handler above makes this VMenu CONTROLLED (Vuetify's
// proxiedModel treats the model as controlled only when both :model-value and
// @update:model-value are bound). Without it, Vuetify's own close paths — Escape
// caught by the overlay's window-level keydown after ArrowDown moves focus into
// the menu, or a click-outside while focus is inside — set VMenu's internal
// isActive to false but never notify the parent. menuIsOpened (driven by
// stickyOpener) then stays true, the :model-value prop never changes, and the
// uncontrolled model never re-syncs — leaving the button stuck on a dead menu.
// Routing every Vuetify close back through emits('close') -> handleHardClose keeps
// the parent's flags and VMenu's state in agreement. An activator click that
// would open the menu is reported as 'open', which a touch device uses (MenuButton).

// Close the menu on page scroll. Vuetify's VMenu otherwise keeps the dropdown
// open and lets it drift off its activator as the page scrolls; nuxt2 closed it.
// The listener is only attached while the menu is open.
const onScroll = () => emits('close')
watch(() => props.open, (isOpen) => {
  if (!import.meta.client) { return }
  if (isOpen) {
    window.addEventListener('scroll', onScroll, { passive: true })
  } else {
    window.removeEventListener('scroll', onScroll)
  }
})
onBeforeUnmount(() => {
  if (import.meta.client) { window.removeEventListener('scroll', onScroll) }
})

</script>

<style lang="scss" scoped>
.menu-content {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  left: -11px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-height: calc(100vh - 124px);
  overflow-y: auto;
  position: relative;
  z-index: 200;
}
</style>
